<script setup>
import { ref, computed } from 'vue';
import { Head, router, useForm, usePage } from '@inertiajs/vue3';
import Layout from '@pages/Shared/layouts/AdminLayout.vue';
import Modal from '@pages/Shared/Modal.vue';
import Input from '@pages/Shared/form/Input.vue';
import Prompt from '@pages/Shared/Prompt.vue';
import Table from '@pages/Shared/ui/Table.vue';
import Spinner from '@pages/Shared/ui/Spinner.vue';
import { PhBuilding } from "@phosphor-icons/vue";
import { flashToast } from "@/toast.js";

const props = defineProps({
    sections: Object,
});

const openModal   = ref(false);
const showPrompt  = ref(false);
const modalStatus = ref(null);
const modalTitle  = ref('');

const form = useForm({
    _method: null,
    id:      null,
    code:    '',
    name:    '',
});

const columns = [
    { key: 'index',   label: '#',    type: 'index'   },
    { key: 'name',    label: 'Name'                  },
    { key: 'code',    label: 'Code'                  },
    { key: 'actions', label: '',     type: 'actions' },
];

const handleSearch = (value) => {
    router.get('/admin/sections', { search: value, filter: 1 }, {
        preserveState: true, preserveScroll: true, replace: true,
    });
};

const setFormData = (section) => {
    form.id   = section.id;
    form.code = section.code;
    form.name = section.name;
};

const editSection = (id) => {
    const section = props.sections.data.find(s => s.id === id);
    if (!section) return;
    setFormData(section);
    modalTitle.value  = 'Edit Section';
    modalStatus.value = 'edit';
    openModal.value   = true;
};

const viewSection = (id) => {
    const section = props.sections.data.find(s => s.id === id);
    if (!section) return;
    setFormData(section);
    modalTitle.value  = 'View Section';
    modalStatus.value = 'view';
    openModal.value   = true;
};

const deleteSection = (id) => { form.id = id; showPrompt.value = true; };

const confirmDelete = () => {
    form.delete(`/admin/sections/${form.id}`, {
        onSuccess: (page) => {
            showPrompt.value = false;
            form.clearErrors();
            form.reset();
            flashToast(page.props.flash);
        }
    });
};

const handleSubmit = () => {
    const options = {
        onSuccess: (page) => {
            form.clearErrors();
            form.reset();
            openModal.value = false;
            flashToast(page.props.flash);
        }
    };
    if (modalStatus.value === 'edit') {
        form._method = 'put';
        form.post(`/admin/sections/${form.id}`, options);
    } else {
        form.post('/admin/sections', options);
    }
};

const handleModalClose = () => {
    modalStatus.value = null;
    modalTitle.value  = null;
    openModal.value   = false;
    form.clearErrors();
    form.reset();
};

const openAddSectionModal = () => {
    form.reset();
    form.clearErrors();
    modalTitle.value  = 'Add Section';
    modalStatus.value = 'create';
    openModal.value   = true;
};

const page = usePage();
const can  = computed(() => page.props.can || {});
</script>

<template>
    <Layout>
        <Head title="Sections Management" />

        <Prompt
            title="section"
            :isShow="showPrompt"
            @cancel="showPrompt = false"
            @delete="confirmDelete"
        />

        <Modal
            v-model="openModal"
            :title="modalTitle"
            @close="handleModalClose"
        >
            <form class="p-4 md:p-5" @submit.prevent="handleSubmit">

                <Input v-model="form.code" type="text" label="Code" placeholder="Section Code"
                    :disabled="modalStatus === 'view'" :error="form.errors.code" class="mb-4" />

                <Input v-model="form.name" type="text" label="Name" placeholder="Section Name"
                    :disabled="modalStatus === 'view'" :error="form.errors.name" class="mb-4" />

                <button
                    v-if="modalStatus !== 'view'"
                    type="submit"
                    class="w-full px-6 py-3 rounded-lg text-white text-sm font-semibold
                           transition flex justify-center items-center gap-2"
                    :class="modalStatus === 'edit'
                        ? 'bg-green-600 hover:bg-green-700'
                        : 'bg-primary hover:bg-primary-light'"
                    :disabled="form.processing"
                >
                    <Spinner v-if="form.processing" :size="14" />
                    {{ modalStatus === 'edit' ? 'Update Section' : 'Add Section' }}
                </button>
            </form>
        </Modal>

        <!-- Header -->
        <div class="flex items-center justify-between mb-6">
            <div>
                <h2 class="text-xl font-bold text-primary">Sections</h2>
                <p class="text-sm text-gray-500 mt-0.5">Manage all sections</p>
            </div>
            <button
                v-if="can.createSection"
                @click="openAddSectionModal"
                class="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold
                       text-white bg-primary hover:bg-primary-light rounded-lg transition"
            >
                <PhBuilding :size="20" />
                Add Section
            </button>
        </div>

        <Table
            :data="sections"
            :columns="columns"
            row-key="id"
            :exportable="true"
            :actions="{ view: true, edit: true, delete: true }"
            @search="handleSearch"
            @edit="editSection"
            @view="viewSection"
            @delete="deleteSection"
        />
    </Layout>
</template>